"use client";

import * as React from "react";
import {
	format,
	isAfter,
	isSameDay,
	endOfDay,
	isBefore,
	startOfDay,
	eachDayOfInterval,
	isWithinInterval,
} from "date-fns";
import { TZDate } from "@date-fns/tz";
import { CalendarIcon, X } from "lucide-react";
import type { DateRange, Matcher } from "react-day-picker";

import { cn } from "../../lib/utils";
import { formatTimezoneLabel, getLocalTimezone } from "../../lib/timezone";
import { Button, type ButtonProps } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { DatePickerCalendar, toZonedMatchers } from "./date-picker-calendar";
import { type DateRangePreset, getPresetRanges } from "./date-range-presets";

export function isDateDisabled(date: Date, matcher: Matcher): boolean {
	if (typeof matcher === "boolean") {
		return matcher;
	}

	if (matcher instanceof Date) {
		return isSameDay(date, matcher);
	}

	if (Array.isArray(matcher)) {
		return matcher.some((item) => isDateDisabled(date, item));
	}

	if (typeof matcher === "object" && matcher !== null && "from" in matcher) {
		const range = matcher as { from?: Date; to?: Date };
		if (range.from && range.to) {
			return isWithinInterval(date, {
				start: startOfDay(range.from),
				end: endOfDay(range.to),
			});
		}
		if (range.from) {
			return isSameDay(date, range.from) || isAfter(date, range.from);
		}
		if (range.to) {
			return isSameDay(date, range.to) || isBefore(date, range.to);
		}
	}

	if (
		typeof matcher === "object" &&
		matcher !== null &&
		"before" in matcher &&
		"after" in matcher
	) {
		const { before, after } = matcher as { before: Date; after: Date };
		return isAfter(date, after) && isBefore(date, before);
	}

	if (typeof matcher === "object" && matcher !== null && "before" in matcher) {
		const { before } = matcher as { before: Date };
		return isBefore(date, before);
	}

	if (typeof matcher === "object" && matcher !== null && "after" in matcher) {
		const { after } = matcher as { after: Date };
		return isAfter(date, after);
	}

	if (
		typeof matcher === "object" &&
		matcher !== null &&
		"dayOfWeek" in matcher
	) {
		const { dayOfWeek } = matcher as { dayOfWeek: number[] };
		return dayOfWeek.includes(date.getDay());
	}

	if (typeof matcher === "function") {
		return matcher(date);
	}

	return false;
}

export function hasDisabledDaysInRange(
	from: Date,
	to: Date,
	disabledDays?: Matcher | Matcher[],
): boolean {
	if (!disabledDays) {
		return false;
	}

	const matchers = Array.isArray(disabledDays) ? disabledDays : [disabledDays];
	const daysInRange = eachDayOfInterval({ start: from, end: to });

	return daysInRange.some((day) =>
		matchers.some((matcher) => isDateDisabled(day, matcher)),
	);
}

/**
 * The calendar's two-click range state machine, shared by {@link DateRangePicker} and
 * {@link FilterDrawerDateRange}.
 *
 * An empty or complete `current` starts a new half-open range; a half-open one is closed by
 * the second click, in whichever order the two days were picked. A span containing a disabled
 * day cannot be closed, so that click starts over instead.
 *
 * `current` is the range the calendar is displaying, in the same (possibly zoned) space as
 * `clickedDate`. Returns the next range, or `null` when the click is a no-op — which is only
 * reachable for a `to` with no `from`, a range no calendar interaction produces.
 *
 * Exported so consumers driving the picker from external state (a URL, a form) can unit-test
 * that round trip against the real click logic rather than a copy of it.
 */
export function resolveDayRangeClick(
	clickedDate: Date,
	current: DateRange | undefined,
	disabledDays?: Matcher | Matcher[],
): DateRange | null {
	if (!current?.from && !current?.to) {
		return { from: startOfDay(clickedDate), to: undefined };
	}

	if (current?.from && !current?.to) {
		if (isSameDay(clickedDate, current.from)) {
			return { from: current.from, to: endOfDay(clickedDate) };
		}

		if (isAfter(clickedDate, current.from)) {
			if (hasDisabledDaysInRange(current.from, clickedDate, disabledDays)) {
				return { from: startOfDay(clickedDate), to: undefined };
			}
			return { from: current.from, to: endOfDay(clickedDate) };
		}

		if (isBefore(clickedDate, current.from)) {
			if (hasDisabledDaysInRange(clickedDate, current.from, disabledDays)) {
				return { from: startOfDay(clickedDate), to: undefined };
			}
			return {
				from: startOfDay(clickedDate),
				to: endOfDay(current.from),
			};
		}
	}

	if (current?.from && current?.to) {
		return { from: startOfDay(clickedDate), to: undefined };
	}

	return null;
}

const DATE_FORMAT = "MMM d, yyyy";

export interface DateRangePickerProps {
	id?: string;
	dateRange?: DateRange;
	onDateRangeChange?: (dateRange: DateRange | undefined) => void;
	onBlur?: (e: React.FocusEvent<HTMLButtonElement>) => void;
	className?: string;
	placeholder?: string;
	minDate?: Date;
	maxDate?: Date;
	isInvalid?: boolean;
	dateFormat?: string;
	datePresets?: DateRangePreset[];
	align?: "center" | "start" | "end";
	disabledDays?: Matcher | Matcher[];
	size?: ButtonProps["size"];
	/** Paints the trigger `outline-primary` for a filter with an applied range. Not for forms. */
	active?: boolean;
	/**
	 * Which moment of each picked calendar day the range boundaries snap to. Applied to both calendar
	 * clicks and preset selections, in the resolved `timezone` when set. Defaults to
	 * `from = startOfDay`, `to = endOfDay` (the picker's historical behaviour).
	 */
	time?: { from: "startOfDay" | "endOfDay"; to: "startOfDay" | "endOfDay" };
	/** Merged into the popover surface (e.g. to control its z-index relative to app chrome). */
	popoverClassName?: string;
	/** Classes for the outer container. Defaults to a `max-w-2xl` cap; pass `max-w-none` for full width. */
	containerClassName?: string;
	/**
	 * When set, the picker interprets and displays the range in this IANA timezone instead of the
	 * browser's. `dateRange` / `onDateRangeChange` stay absolute instants. Leave undefined for
	 * browser-timezone behaviour.
	 */
	timezone?: string;
	/** Optional small helper text rendered beneath the picker (e.g. naming the recorded timezone). */
	hint?: string;
}

export function DateRangePicker({
	id,
	dateRange: controlledDateRange,
	onDateRangeChange,
	onBlur,
	className,
	placeholder,
	minDate,
	maxDate,
	isInvalid,
	dateFormat = DATE_FORMAT,
	datePresets,
	align = "start",
	size = "sm",
	active = false,
	disabledDays,
	time,
	popoverClassName,
	containerClassName,
	timezone,
	hint,
}: DateRangePickerProps) {
	const [internalDateRange, setInternalDateRange] = React.useState<
		DateRange | undefined
	>(undefined);
	const [open, setOpen] = React.useState(false);

	// Internal state and the public `dateRange` / `onDateRangeChange` contract are always absolute
	// instants. The calendar, presets, and label work on zone-tagged `TZDate`s (via react-day-picker's
	// native `timeZone`), so we wrap instants into that zone for rendering and collapse back to plain
	// instants on change. Keeping internal state absolute means a later `timezone` change re-renders
	// the saved range rather than reinterpreting it. Both helpers are identity when no timezone is set.
	const toZoned = React.useCallback(
		(d?: Date) => (timezone && d ? new TZDate(d.getTime(), timezone) : d),
		[timezone],
	);
	const toInstant = (d?: Date) => (timezone && d ? new Date(d.getTime()) : d);
	const toZonedRange = (r?: DateRange): DateRange | undefined =>
		r ? { from: toZoned(r.from), to: toZoned(r.to) } : r;
	const toInstantRange = (r?: DateRange): DateRange | undefined =>
		r ? { from: toInstant(r.from), to: toInstant(r.to) } : r;

	// A `TZDate`'s getters return the in-zone wall clock, so plain `format` renders the correct day for
	// both the browser and timezone cases.
	const formatDate = (date: Date) => format(date, dateFormat);

	// Zone the disabled matcher so the calendar and the `hasDisabledDaysInRange` click-guard evaluate
	// it against the same fund-zone day cells as everything else (clicks/presets arrive zoned too).
	const zonedDisabledDays = toZonedMatchers(disabledDays, timezone);

	// Always surface the zone the popover is operating in (the explicit `timezone` or the viewer's
	// local zone), resolved against the range start so DST is reflected.
	const timezoneLabel = formatTimezoneLabel(
		timezone ?? getLocalTimezone(),
		controlledDateRange?.from ?? internalDateRange?.from,
	);

	// Snap a picked (zoned) day to the configured boundary; `startOfDay`/`endOfDay` respect the
	// `TZDate`'s zone, so the boundary lands in the resolved timezone. Defaults preserve the picker's
	// historical `from = startOfDay`, `to = endOfDay` behaviour.
	const applyTime = (d: Date | undefined, which: "startOfDay" | "endOfDay") =>
		d ? (which === "endOfDay" ? endOfDay(d) : startOfDay(d)) : d;

	// Convert the absolute range (controlled or internal) into zoned space for the calendar and label.
	const dateRange = toZonedRange(controlledDateRange ?? internalDateRange);
	// `range` arrives zoned (from calendar clicks / presets); snap boundaries per `time`, then store
	// and emit absolute instants.
	const setDateRange = (range: DateRange | undefined) => {
		const snapped: DateRange | undefined = range
			? {
					from: applyTime(range.from, time?.from ?? "startOfDay"),
					to: applyTime(range.to, time?.to ?? "endOfDay"),
				}
			: range;
		const absoluteRange = toInstantRange(snapped);
		setInternalDateRange(absoluteRange);
		onDateRangeChange?.(absoluteRange);
	};

	const customPresets = React.useMemo(() => {
		// Compute presets in the resolved timezone's calendar so "Today"/"This Month"/etc. mean that
		// zone's days (not the browser's). Boundaries come back as `TZDate`s and are collapsed to
		// absolute instants on select via `toInstantRange` — just like calendar clicks.
		// eslint-disable-next-line react-hooks/purity -- preset boundaries are defined in the fund timezone's current calendar day
		const now = timezone ? new TZDate(Date.now(), timezone) : undefined;
		return getPresetRanges({
			minDate: toZoned(minDate),
			maxDate: toZoned(maxDate),
			options: datePresets,
			now,
			timeZone: timezone,
		});
	}, [minDate, maxDate, datePresets, timezone, toZoned]);

	const handlePresetSelect = (presetValue: string) => {
		const preset = customPresets.find((p) => p.value === presetValue);
		if (preset) {
			// Presets are already computed in view space (see `customPresets`), so store them directly.
			setDateRange(preset.getRange());
		}
	};

	const handleClearSelection = () => {
		setDateRange(undefined);
	};

	const handleDayClick = (clickedDate: Date) => {
		const next = resolveDayRangeClick(
			clickedDate,
			dateRange,
			zonedDisabledDays,
		);
		if (next) {
			setDateRange(next);
		}
	};

	const formatDateRange = () => {
		if (!dateRange?.from) {
			return placeholder;
		}

		if (dateRange.from && dateRange.to) {
			return `${formatDate(dateRange.from)} \u2192 ${formatDate(dateRange.to)}`;
		}

		return `${formatDate(dateRange.from)} \u2192`;
	};

	return (
		<>
			<div className={cn("w-full max-w-2xl space-y-4", containerClassName)}>
				<Popover open={open} onOpenChange={setOpen}>
					<PopoverTrigger asChild>
						<Button
							id={id}
							variant={active ? "filter-active" : "outline"}
							className={cn(
								"w-full group rounded pl-3 pr-2 text-left text-xs",
								!active &&
									"text-neutral-600 hover:bg-neutral-50 hover:text-neutral-600",
								isInvalid && "outline-danger outline-1 outline",
								className,
							)}
							onBlur={onBlur}
							size={size}
						>
							<CalendarIcon className="h-4 w-4 text-neutral-400" />
							<span className="flex-1 truncate">{formatDateRange()}</span>
							{dateRange?.from && (
								<div
									role="button"
									aria-label="Clear date range"
									tabIndex={-1}
									className="flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
									onClick={(e) => {
										e.stopPropagation();
										handleClearSelection();
									}}
									onKeyDown={(e) => {
										if (e.key !== "Enter" && e.key !== " ") {
											return;
										}
										e.preventDefault();
										e.stopPropagation();
										handleClearSelection();
									}}
								>
									<X className="h-2 w-2 text-neutral-500" />
								</div>
							)}
						</Button>
					</PopoverTrigger>
					<PopoverContent
						className={cn("w-auto p-0", popoverClassName)}
						align={align}
					>
						<div
							data-testid="date-range-picker-popover"
							className="flex h-[330px]"
						>
							{customPresets.length > 0 && (
								<div
									data-testid="date-range-presets"
									className="border-r border-neutral-200 p-3 w-[180px] max-h-[330px] overflow-y-auto"
								>
									<div className="space-y-1">
										{customPresets.map((preset) => (
											<Button
												key={preset.value}
												variant="ghost"
												size="sm"
												disabled={!preset.isValid}
												className="w-full justify-start text-xs text-neutral-700 font-medium h-8"
												onClick={() => {
													handlePresetSelect(preset.value);
													setOpen(false);
												}}
											>
												{preset.label}
											</Button>
										))}
									</div>
								</div>
							)}

							<div className="flex flex-col max-h-[330px] justify-between">
								<DatePickerCalendar
									mode="range"
									dateRange={dateRange}
									onDateRangeChange={setDateRange}
									onDayClick={handleDayClick}
									minDate={toZoned(minDate)}
									maxDate={toZoned(maxDate)}
									disabledDays={zonedDisabledDays}
									timeZone={timezone}
								/>
							</div>
						</div>
						<div className="border-neutral-200 border-t py-2 text-center text-xs text-neutral-500">
							{timezoneLabel}
						</div>
					</PopoverContent>
				</Popover>
			</div>
			{hint && <p className="mt-1.5 text-xs text-neutral-500">{hint}</p>}
		</>
	);
}
